if (!customElements.get('td-complementary-products')) {
  class TdComplementaryProducts extends HTMLElement {
    static SELECTORS = {
      track: '[data-td-complementary-track]',
      loading: '[data-td-complementary-loading]',
      footer: '[data-td-complementary-footer]',
      addAll: '[data-td-complementary-add-all]',
      addButton: '[data-td-complementary-add]',
    };

    static SECTION_ID = 'td-complementary-cards';

    static ADD_PROPERTY = '_complementary_add';

    static ADD_SOURCE = {
      single: 'single',
      bundle: 'bundle',
    };

    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.abortController = null;
      // Cart contexts render multiple source product ids into `data-td-product-id` and
      // morph this element in place on cart updates, so watch for that attribute changing
      // (e.g. the cart's distinct product set changed) and refetch when it does. A plain
      // quantity change leaves the attribute value identical, so no request is wasted.
      this.attributeObserver = new MutationObserver(this.#handleAttributeChange);
      this.lastProductIds = null;
    }

    connectedCallback() {
      if (this.closest('.quick-add-modal, .quick-add-modal__content')) {
        this.hidden = true;
        return;
      }

      this.track = this.querySelector(TdComplementaryProducts.SELECTORS.track);
      this.loadingNode = this.querySelector(TdComplementaryProducts.SELECTORS.loading);
      this.addAllButton = this.querySelector(TdComplementaryProducts.SELECTORS.addAll);
      this.addEventListener('click', this.handleClick);
      this.lastProductIds = this.dataset.tdProductId || '';
      this.attributeObserver.observe(this, { attributes: true, attributeFilter: ['data-td-product-id'] });

      const slotsNeeded = Number(this.dataset.tdSlotsNeeded) || 0;
      const existingCount = Number(this.dataset.tdCardsCount) || 0;

      if (slotsNeeded > 0) {
        if (existingCount === 0) {
          this.dataset.tdVisibility = 'pending';
        }
        this.fetchComplementaryProducts(slotsNeeded);
      } else {
        this.hidden = false;
        this.dataset.tdVisibility = 'visible';
        this.dataset.tdReady = '';
        this.syncAddAllState();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.attributeObserver.disconnect();

      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    /**
     * Refetches when the cart's distinct product set changes underneath a morphed element.
     * @param {MutationRecord[]} mutations
     */
    #handleAttributeChange = (mutations) => {
      if (!mutations.some((mutation) => mutation.attributeName === 'data-td-product-id')) return;

      const newProductIds = this.dataset.tdProductId || '';
      if (newProductIds === this.lastProductIds) return;
      this.lastProductIds = newProductIds;

      this.abortController?.abort();
      this.abortController = null;

      if (this.track) this.track.replaceChildren();

      this.dataset.tdCardsCount = '0';
      this.dataset.tdSeenIds = '';
      this.dataset.tdVisibility = 'pending';
      delete this.dataset.tdReady;
      this.hidden = true;
      if (this.loadingNode) this.loadingNode.hidden = false;

      const slotsNeeded = Number(this.dataset.tdSlotsNeeded) || 0;
      if (slotsNeeded > 0) {
        this.fetchComplementaryProducts(slotsNeeded);
      } else {
        this.hideComponent();
      }
    };

    async handleClick(event) {
      const addButton = event.target.closest(TdComplementaryProducts.SELECTORS.addButton);

      if (addButton) {
        event.preventDefault();
        const variantId = addButton.dataset.tdVariantId;
        if (!variantId || addButton.disabled) return;
        await this.addItems(
          [{ id: Number(variantId), quantity: 1 }],
          addButton,
          TdComplementaryProducts.ADD_SOURCE.single
        );
        return;
      }

      const addAllButton = event.target.closest(TdComplementaryProducts.SELECTORS.addAll);

      if (addAllButton) {
        event.preventDefault();
        if (addAllButton.disabled) return;

        const meta = this.getRenderedItemsMeta();
        if (meta.items.length === 0) return;
        await this.addItems(meta.items, addAllButton, TdComplementaryProducts.ADD_SOURCE.bundle);
      }
    }

    async fetchComplementaryProducts(slotsNeeded) {
      const endpointUrl = this.dataset.tdEndpointUrl;
      const productIds = (this.dataset.tdProductId || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      if (!this.track || !endpointUrl || productIds.length === 0) {
        this.hideComponent();
        return;
      }

      // Defaults to 'complementary' so existing single-product callers (e.g. the PDP block)
      // that never set this attribute keep their original behavior unchanged. This is the
      // merchant's chosen intent and is used as-is, with no fallback to the other intent:
      // a product with no data for the selected intent simply contributes no cards.
      const intent = this.dataset.tdIntent || 'complementary';

      const abortController = new AbortController();
      this.abortController = abortController;

      try {
        // A single source product still gets the larger buffer the original PDP flow relied
        // on to skip curated duplicates; multiple sources fetch leaner since results are merged.
        const perProductLimit = productIds.length > 1 ? slotsNeeded : slotsNeeded + 4;

        /**
         * @param {string} productId
         */
        const requestCards = (productId) => {
          const endpoint = `${endpointUrl}?product_id=${encodeURIComponent(productId)}&intent=${encodeURIComponent(intent)}&limit=${perProductLimit}&section_id=${TdComplementaryProducts.SECTION_ID}`;
          return fetch(endpoint, { signal: abortController.signal })
            .then((response) => {
              if (!response.ok) throw new Error('Complementary endpoint request failed.');
              return response.text();
            })
            .then((html) =>
              Array.from(new DOMParser().parseFromString(html, 'text/html').querySelectorAll('.td-complementary-products__slide'))
            );
        };

        const cardsByProduct = await Promise.all(
          productIds.map((productId) =>
            requestCards(productId).catch((error) => {
              if (error.name === 'AbortError') throw error;
              return [];
            })
          )
        );

        // Cart products are also the fetch sources, so excluding them here satisfies
        // "never recommend a product already in the cart" for free.
        const seenIds = new Set((this.dataset.tdSeenIds || '').split('|').filter(Boolean).concat(productIds));

        // Round-robin across source products so no single one dominates the merged carousel.
        const fragment = document.createDocumentFragment();
        let added = 0;
        for (let round = 0; added < slotsNeeded; round++) {
          let tookAny = false;

          for (const cards of cardsByProduct) {
            if (added >= slotsNeeded) break;

            const card = cards[round];
            if (!card) continue;
            tookAny = true;

            const cardProductId = card.dataset.tdProductId;
            if (cardProductId && seenIds.has(cardProductId)) continue;

            if (cardProductId) seenIds.add(cardProductId);
            fragment.appendChild(card);
            added++;
          }

          if (!tookAny) break;
        }

        const existingCount = Number(this.dataset.tdCardsCount) || 0;

        if (added === 0 && existingCount === 0) {
          this.hideComponent();
          return;
        }

        if (added > 0) {
          this.track.appendChild(fragment);
        }

        this.hidden = false;
        this.dataset.tdVisibility = 'visible';
        this.dataset.tdReady = '';
        this.dataset.tdCardsCount = String(existingCount + added);

        this.syncAddAllState();

        const emblaSlider = this.querySelector('embla-slider');
        if (emblaSlider?.emblaApi && typeof emblaSlider.emblaApi.reInit === 'function') {
          emblaSlider.emblaApi.reInit();
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        const existingCount = Number(this.dataset.tdCardsCount) || 0;
        if (existingCount === 0) this.hideComponent();
      } finally {
        if (this.loadingNode) this.loadingNode.hidden = true;
        // Only clear the controller if a newer fetch (from a later attribute change)
        // hasn't already replaced it — otherwise this stale call's cleanup would wipe
        // out the reference the in-flight newer fetch needs for its own abort/cleanup.
        if (this.abortController === abortController) this.abortController = null;
      }
    }

    getRenderedItemsMeta() {
      const seenVariantIds = new Set();
      const items = [];
      let totalCents = 0;

      for (const button of this.querySelectorAll(`${TdComplementaryProducts.SELECTORS.addButton}[data-td-variant-id]`)) {
        const variantId = Number(button.dataset.tdVariantId);
        if (!variantId || seenVariantIds.has(variantId)) continue;

        seenVariantIds.add(variantId);
        items.push({ id: variantId, quantity: 1 });
        totalCents += Number(button.dataset.tdPriceCents) || 0;
      }

      return { items, count: items.length, totalCents };
    }

    async addItems(items, triggerButton, addSource) {
      const originalLabel = triggerButton?.textContent || '';
      if (triggerButton) triggerButton.disabled = true;

      try {
        const propertyKey = TdComplementaryProducts.ADD_PROPERTY;
        const itemsWithProperties = items.map((item) => ({
          ...item,
          properties: {
            ...(item.properties || {}),
            [propertyKey]: addSource,
          },
        }));
        const routesRoot = this.dataset.tdRoutesRoot || '/';
        const addResponse = await fetch(`${routesRoot}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: itemsWithProperties }),
        });

        if (!addResponse.ok) throw new Error('Unable to add complementary products to cart.');
        await this.refreshCartUI();
      } catch (error) {
        console.error(error);
      } finally {
        if (triggerButton) {
          triggerButton.disabled = false;

          if (triggerButton.hasAttribute('data-td-complementary-add-all')) {
            triggerButton.textContent = originalLabel;
          }
        }
      }
    }

    async refreshCartUI() {
      const routesRoot = this.dataset.tdRoutesRoot || '/';
      const [cartResponse, sectionsResponse] = await Promise.all([
        fetch(`${routesRoot}cart.js`, { headers: { Accept: 'application/json' } }),
        fetch(`${routesRoot}?sections=cart-drawer,cart-icon-bubble`, { headers: { Accept: 'application/json' } }),
      ]);

      if (!cartResponse.ok || !sectionsResponse.ok) return;

      const cart = await cartResponse.json();
      const sections = await sectionsResponse.json();

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'td-complementary-products',
            data: { itemCount: cart?.item_count, sections },
          },
        })
      );

      const drawer = document.querySelector('cart-drawer-component');

      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      } else if (drawer && typeof drawer.openDrawer === 'function') {
        drawer.openDrawer();
      }
    }

    syncAddAllState() {
      const footer = this.querySelector(TdComplementaryProducts.SELECTORS.footer);
      if (!footer || !this.addAllButton) return;

      const meta = this.getRenderedItemsMeta();

      if (meta.count < 2) {
        footer.hidden = true;
      } else {
        footer.hidden = false;
        this.addAllButton.textContent = this.getAddAllButtonText(meta.count, meta.totalCents);
      }
    }

    getAddAllButtonText(count, totalCents) {
      const template = this.dataset.tdAddAllLabelTemplate || '';
      const countLabel = template.replace(/\[count\]/gi, String(count));
      return `${countLabel} — ${this.formatMoney(totalCents)}`;
    }

    formatMoney(cents) {
      const amount = (Number(cents) || 0) / 100;
      const currency = this.dataset.tdCurrency || 'USD';
      const locale = document.documentElement.lang || 'en-US';

      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
      } catch {
        return amount.toFixed(2);
      }
    }

    hideComponent() {
      this.hidden = true;
      this.dataset.tdVisibility = 'hidden';
    }
  }

  customElements.define('td-complementary-products', TdComplementaryProducts);
}
